import { EmptyState } from '@/components/EmptyState';
import { LoadMoreButton } from '@/components/LoadMoreButton';
import { ShiftBadge } from '@/components/shifts/ShiftBadge';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatClock, formatDurationHm, formatLocalDate } from '@/lib/format';
import type { AdminEntry, Entry } from '@/types/projects';

interface ProjectEntriesTableProps {
  entries: Array<Entry | AdminEntry>;
  showPerson: boolean;
  loading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}

function personOf(entry: Entry | AdminEntry): AdminEntry['user'] | null {
  return 'user' in entry ? entry.user : null;
}

/** Read-only entries list (Date, Start–End, Duration, Description), keyset "Load more". */
export function ProjectEntriesTable({ entries, showPerson, loading, hasMore, loadingMore, onLoadMore }: ProjectEntriesTableProps): JSX.Element {
  if (loading) return <Skeleton className="h-32 w-full" />;
  if (entries.length === 0) return <EmptyState title="No time logged for this project yet." />;
  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            {showPerson && <TableHead>Person</TableHead>}
            <TableHead>Date</TableHead>
            <TableHead>Start–End</TableHead>
            <TableHead className="text-right">Duration</TableHead>
            <TableHead>Description</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((e) => {
            const person = personOf(e);
            return (
              <TableRow key={e.id}>
                {showPerson && (
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5">
                      {person?.name}
                      {person?.status === 'deactivated' && <Badge>Deactivated</Badge>}
                    </span>
                  </TableCell>
                )}
                <TableCell className="whitespace-nowrap font-mono tabular-nums">{formatLocalDate(e.date)}</TableCell>
                <TableCell className="whitespace-nowrap font-mono tabular-nums">
                  {formatClock(e.startSecond)}–{formatClock(e.endSecond)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">{formatDurationHm(e.durationSeconds)}</TableCell>
                <TableCell className="text-muted-foreground">{e.shiftId ? <ShiftBadge /> : (e.description ?? '')}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <LoadMoreButton hasMore={hasMore} loading={loadingMore} onClick={onLoadMore} />
    </div>
  );
}
